// Stock by location comes from the variant metafield waller_dispatch.stock_by_location (written by Shopify Flow).
function inventoryFromPage(data, expectedProductId) {
  if (data?.source !== 'shopify-flow' || String(data.productId) !== String(expectedProductId) || !data.variants || typeof data.variants !== 'object') throw new Error('Independent stock response unavailable.');
  const inventory = {};
  for (const [variantId, current] of Object.entries(data.variants)) {
    if (!Number.isSafeInteger(current.inventoryQuantity) || !['deny', 'continue'].includes(current.policy)) continue;
    const snapshot = current.snapshot;
    const valid = current.tracked && [1, 2].includes(snapshot?.version) && String(snapshot.variantId) === variantId && snapshot.tracked === true && snapshot.inventoryQuantity === current.inventoryQuantity && Array.isArray(snapshot.levels);
    let levels;
    if (valid) {
      const ids = new Set();
      let invalid = false;
      for (const level of snapshot.levels) {
        const id = String(level.locationId);
        if (!/^\d+$/.test(id) || ids.has(id) || !Number.isSafeInteger(level.available) || typeof level.active !== 'boolean') invalid = true;
        ids.add(id);
      }
      if (invalid) continue;
      levels = snapshot.levels.filter(level => level.active).map(level => ({locationId: String(level.locationId), available: level.available}));
    } else if (current.tracked && current.inventoryQuantity <= 0) {
      levels = [];
    } else if (!current.tracked) {
      levels = [];
    } else {
      continue;
    }
    levels.policy = current.policy;
    levels.tracked = current.tracked;
    levels.inventoryQuantity = current.inventoryQuantity;
    inventory[variantId] = levels;
  }
  return inventory;
}


// Generated by dispatch-draft/build-browser.mjs. Single asset prevents stale dependency imports.
function estimateStock(lines, inventory, configuration) {
  const grouped = new Map();
  for (const line of lines) {
    if (line.requiresShipping === false) continue;
    if (!Number.isSafeInteger(line.quantity) || line.quantity < 1 || !line.variantId) {
      throw new TypeError('Each shipping line needs a variant ID and a positive integer quantity.');
    }
    const id = String(line.variantId);
    const current = grouped.get(id) ?? {variantId:id, quantity:0, preorder:false};
    current.quantity += line.quantity;
    current.preorder ||= line.preorder === true;
    grouped.set(id,current);
  }
  const results = [];
  let local = false;
  let supplier = false;
  const rules = new Map(configuration.locations.map(rule => [String(rule.id),rule]));
  for (const line of grouped.values()) {
    if (line.preorder) {
      results.push({...line,status:'unknown',reason:'Preorder needs its own dispatch estimate.'});
      continue;
    }
    const levels = inventory[line.variantId];
    if (!Array.isArray(levels)) {
      results.push({...line,status:'unknown',reason:'Stock data unavailable.'});
      continue;
    }
    const stock = new Map();
    let invalid = false;
    for (const level of levels) {
      const id = String(level.locationId);
      if (stock.has(id) || !Number.isSafeInteger(level.available)) invalid = true;
      stock.set(id,Math.max(0,level.available));
    }
    if (invalid) {
      results.push({...line,status:'unknown',reason:'Stock data needs checking.'});
      continue;
    }
    const localRule = rules.get(String(configuration.wallerLocationId));
    if (!localRule || localRule.enabled === false) throw new Error('Waller location rule is required.');
    const localQty = Math.min(stock.get(String(configuration.wallerLocationId)) ?? 0,line.quantity);
    let remaining = line.quantity-localQty;
    const allocations = localQty ? [{locationId:localRule.id,quantity:localQty,min:0,max:1,source:'waller'}] : [];
    if (remaining > 0) {
      const candidates = configuration.locations.filter(rule => rule.enabled !== false && String(rule.id) !== String(configuration.wallerLocationId) && (stock.get(String(rule.id)) ?? 0) > 0);
      // Prefer the shortest complete dispatch window, then its earliest start.
      // Use the location ID as a stable tie-break when lead times are equal.
      candidates.sort((a,b)=>a.max-b.max || a.min-b.min || String(a.id).localeCompare(String(b.id)));
      for (const rule of candidates) {
        if (!remaining) break;
        if (!Number.isInteger(rule.min) || !Number.isInteger(rule.max) || rule.min < 0 || rule.max < rule.min) throw new Error('Invalid supplier dispatch range.');
        const quantity = Math.min(stock.get(String(rule.id)),remaining);
        allocations.push({locationId:rule.id,quantity,min:rule.min,max:rule.max,source:'supplier'});
        remaining -= quantity;
      }
    }
    if (remaining) {
      results.push({...line,status:'unknown',reason:'Configured available stock does not cover the requested quantity.'});
      continue;
    }
    local ||= allocations.some(a=>a.source==='waller');
    supplier ||= allocations.some(a=>a.source==='supplier');
    results.push({...line,status:'known',allocations,min:Math.max(...allocations.map(a=>a.min)),max:Math.max(...allocations.map(a=>a.max))});
  }
  if (!results.length) return {status:'no_shipping',items:[],message:''};
  if (results.some(line=>line.status==='unknown')) return {status:'unknown',items:results,message:'Please check the dispatch estimate for this order before choosing delivery. Courier time starts after dispatch.'};
  const min = Math.max(...results.map(line=>line.min));
  const max = Math.max(...results.map(line=>line.max));
  const type = local && supplier ? 'mixed' : supplier ? 'supplier' : 'waller';
  const message = type==='waller'
    ? 'All items are in stock at Waller BMX. Dispatch within 1 working day.'
    : type==='mixed'
      ? `Your order includes Waller and supplier stock. All items will be sent together once ready. Estimated dispatch: ${min}–${max} working days. Courier delivery time follows this.`
      : `Estimated dispatch: ${min}–${max} working days. All items will be sent together once ready. Courier delivery time follows this.`;
  return {status:'known',type,min,max,items:results,message};
}


// Expand bundle packs into physical units before allocating any inventory.
// Loose components and packs share one quantity total, so stock is never reused.
function estimateDispatch(lines, inventory, configuration) {
  const bundles = configuration.bundles || {};
  if (!lines.some(line=>bundles[String(line.variantId)] && line.requiresShipping!==false)) return estimateStock(lines,inventory,configuration);
  const expanded = [];
  const parents = new Map();
  for (const line of lines) {
    if (line.requiresShipping===false) continue;
    if (!Number.isSafeInteger(line.quantity) || line.quantity<1) throw new TypeError('Invalid bundle quantity.');
    const id=String(line.variantId);
    const components=bundles[id]?.components || [{variantId:id,quantity:1}];
    const parent=parents.get(id) || {...line,variantId:id,quantity:0,components};
    parent.quantity+=line.quantity;parents.set(id,parent);
    for (const component of components) {
      if (!Number.isSafeInteger(component.quantity) || component.quantity<1 || bundles[String(component.variantId)]) throw new TypeError('Invalid or nested bundle definition.');
      expanded.push({variantId:String(component.variantId),quantity:line.quantity*component.quantity,preorder:line.preorder});
    }
  }
  const result=estimateStock(expanded,inventory,configuration);
  const byId=new Map(result.items.map(item=>[item.variantId,item]));
  const items=[...parents.values()].map(parent=>{
    const components=parent.components.map(component=>byId.get(String(component.variantId)));
    const unresolved=components.find(item=>item?.status!=='known');
    const {components:definition,...line}=parent;
    if (unresolved) return {...line,status:'unknown',reason:unresolved.reason};
    return {...line,status:'known',min:Math.max(...components.map(item=>item.min)),max:Math.max(...components.map(item=>item.max)),allocations:components.flatMap(item=>item.allocations)};
  });
  return {...result,items};
}


// STOQ copy stays authoritative; never turn a free-text estimate into a promised date.
function estimateHasPassed(text,plan={},now=new Date()) {
  const parts=Object.fromEntries(new Intl.DateTimeFormat('en-GB',{timeZone:'Europe/London',year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(now).map(p=>[p.type,p.value]));
  const today=Number(parts.year+parts.month+parts.day);
  const months=['january','february','march','april','may','june','july','august','september','october','november','december'];
  const lower=String(text).toLowerCase();
  const iso=lower.match(/\b(20\d{2})-(\d{2})-(\d{2})\b/);
  if(iso){const [year,month,day]=iso.slice(1).map(Number);const date=new Date(Date.UTC(year,month-1,day));if(date.getUTCFullYear()!==year || date.getUTCMonth()!==month-1 || date.getUTCDate()!==day)return null;return year*10000+month*100+day<today;}
  const monthPattern=months.join('|');
  const found=lower.match(new RegExp('\\b('+monthPattern+')\\b'));
  if(!found)return /\b20\d{2}\b|\b(?:jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)\b|\d{1,2}\/\d{1,2}|\d{1,2}-\d{1,2}-\d{2,4}/.test(lower)?null:false; // Relative lead times such as "within 7 days" are not dated estimates.
  if([...lower.matchAll(new RegExp('\\b('+monthPattern+')\\b','g'))].length>1)return null;
  const month=months.indexOf(found[1])+1;
  const explicit=lower.match(/\b(20\d{2})\b/);
  const offerYear=String(plan.name || '').match(/\b(20\d{2})\b/);
  const updatedYear=String(plan.updated_at || '').match(/^(20\d{2})-/);
  const year=Number(explicit?.[1] || offerYear?.[1] || updatedYear?.[1]);
  if(!year)return null; // A yearless calendar estimate with no offer context is ambiguous.
  const before=lower.match(new RegExp('(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:'+monthPattern+')\\b'));
  const after=lower.match(new RegExp('(?:'+monthPattern+')\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,|\\s|$)'));
  const lastDay=new Date(Date.UTC(year,month,0)).getUTCDate();
  const day=Number(before?.[1] || after?.[1] || lastDay);
  if(day<1 || day>lastDay)return null;
  return year*10000+month*100+day<today;
}

function preorderFor(variantId,levels,plans,{policy,shippingOverride,marketId,selectedPlanId,now=new Date()}={}) {
  if (levels?.tracked === false) return null;
  if(policy!=='continue' || !Array.isArray(levels) || (!levels.length && !(Number.isSafeInteger(levels.inventoryQuantity) && levels.inventoryQuantity <= 0)) || levels.some(l=>!Number.isSafeInteger(l.available) || l.available>0))return null;
  const candidates=plans.filter(p=>p.enabled===true && Array.isArray(p.variant_ids) && p.variant_ids.some(id=>String(id)===String(variantId)) && (!p.markets_enabled || p.shopify_market_ids?.some(id=>String(id)===String(marketId))));
  let applicable=candidates;
  if(selectedPlanId){const selected=candidates.filter(p=>String(p.shopify_selling_plan_id)===String(selectedPlanId)||p.payment_options?.some(o=>String(o.shopify_selling_plan_id)===String(selectedPlanId)));if(selected.length)applicable=selected;}
  if(!applicable.length)return null;
  const texts=applicable.map(p=>p.shipping_applies_to_all_products===false && shippingOverride ? shippingOverride : p.preorder_shipping_text || p.shipping_text || '');
  const unique=[...new Set(texts.map(t=>String(t).trim()))];
  const passed=unique.length===1 ? estimateHasPassed(unique[0],applicable[0],now) : null;
  const estimateState=passed===true?'expired':passed===null?'unconfirmed':'current';
  const shippingText=passed===false && unique.length===1 && unique[0] && !/[<{]|\}\}/.test(unique[0])?unique[0]:'';
  return {status:'preorder',type:'preorder',shippingText,estimateState};
}

function withPreorders(result,overrides) {
  const items=result.items?.map(item=>overrides[item.variantId] ? {...item,...overrides[item.variantId]} : item) || [];
  if(!items.some(item=>item.status==='preorder'))return result;
  if(items.some(item=>item.status==='unknown'))return {...result,items,status:'unknown'};
  return {status:'preorder',type:'preorder',items};
}

function soldOutWithoutPreorder(variantId,levels,plans,{policy,marketId}={}) {
  if (levels?.tracked === false) return false;
  if(policy!=='deny' || !Array.isArray(levels) || (!levels.length && !(Number.isSafeInteger(levels.inventoryQuantity) && levels.inventoryQuantity <= 0)) || levels.some(l=>!Number.isSafeInteger(l.available) || l.available>0))return false;
  return !plans.some(p=>p.enabled===true && p.variant_ids?.some(id=>String(id)===String(variantId)) && (!p.markets_enabled || p.shopify_market_ids?.some(id=>String(id)===String(marketId))));
}


const configuration = {"wallerLocationId": "50698125478", "locations": [{"id": "107527078267", "name": "4down Distribution Location", "enabled": true, "min": 4, "max": 8}, {"id": "107527045499", "name": "Back Street Distribution Location", "enabled": true, "min": 2, "max": 6}, {"id": "56106188966", "name": "IMG Distribution Location", "enabled": true, "min": 4, "max": 8}, {"id": "107527242107", "name": "Ison Distribution Location", "enabled": true, "min": 4, "max": 8}, {"id": "107527143803", "name": "Mafia Bikes Distribution Location", "enabled": true, "min": 3, "max": 5}, {"id": "107527274875", "name": "Profile Europe Location", "enabled": true, "min": 1, "max": 2}, {"id": "107527012731", "name": "Seventies Distro Location", "enabled": true, "min": 4, "max": 8}, {"id": "107526979963", "name": "Traffic Germany Warehouse", "enabled": true, "min": 7, "max": 14}, {"id": "107443519867", "name": "Traffic UK Warehouse", "enabled": true, "min": 4, "max": 8}, {"id": "107527111035", "name": "Upgrade Distribution Location", "enabled": true, "min": 4, "max": 8}, {"id": "50698125478", "name": "Waller BMX LTD, Sheffield", "enabled": true, "min": 0, "max": 1}, {"id": "107535499643", "name": "Zyro Fisher Location", "enabled": true, "min": 4, "max": 8}]};

// Verified Shopify bundle composition. Inventory is fetched live for the component.
configuration.bundles = {"56470638199163": {components: [{variantId:"42417321804025",quantity:3,productId:"7538810454265",handle:"federal-innertube-20-x-2-125-2-4"}]}};



const strings = JSON.parse(document.getElementById('waller-dispatch-strings')?.textContent || '{}');
const stoqData = JSON.parse(document.getElementById('waller-dispatch-stoq')?.textContent || '{}');
const root = window.Shopify?.routes?.root || '/';
const cache = new Map();
let revision = 0;
let timer;
const words = (key,result={}) => (strings[key] || '').replaceAll('__MIN__',String(result.min ?? '')).replaceAll('__MAX__',String(result.max ?? ''));

async function stockFor(handle, productId) {
  const key = String(productId);
  const previous = cache.get(key);
  if (previous && Date.now() - previous.time < 10000) return previous.promise;
  const promise = (async () => {
    if (!handle) throw new Error('Product handle is required.');
    const inventory = {};
    let page = 1;
    while (page !== null) {
      if (!Number.isSafeInteger(page) || page < 1 || page > 20) throw new Error('Invalid stock pagination.');
      const url = new URL(root + 'products/' + encodeURIComponent(handle), location.origin);
      url.searchParams.set('view', 'waller-dispatch-stock');
      url.searchParams.set('page', String(page));
      const response = await fetch(url, {signal: AbortSignal.timeout(8000), cache: 'no-store', credentials: 'same-origin'});
      if (!response.ok) throw new Error('Shopify stock fields unavailable.');
      const data = await response.json();
      Object.assign(inventory, inventoryFromPage(data, key));
      if (data.nextPage !== null && data.nextPage !== page + 1) throw new Error('Invalid next stock page.');
      page = data.nextPage;
    }
    return inventory;
  })();
  cache.set(key, {time: Date.now(), promise});
  promise.catch(() => cache.delete(key));
  return promise;
}


async function inventoryFor(lines) {
  const inventory={};
  const products=new Map();
  for (const line of lines) {
    const components=configuration.bundles?.[String(line.variantId)]?.components;
    for (const product of components || [line]) products.set(String(product.productId),{id:product.productId,handle:product.handle});
  }
  await Promise.all([...products.values()].map(async product=>{
    try {Object.assign(inventory,await stockFor(product.handle,product.id));}catch{}
  }));
  return inventory;
}

const stockShortage = item => item?.status==='unknown' && item.reason==='Configured available stock does not cover the requested quantity.';

function preorderEstimate(variantId,inventory,selectedPlanId) {
  const config=window._RestockRocketConfig || {};
  let livePlan;
  try{livePlan=window._RestockRocket?.getSellingPlan?.(String(variantId));}catch{}
  const plans=Array.isArray(config.sellingPlans) && config.sellingPlans.length ? config.sellingPlans : stoqData.plans || [];
  const source=livePlan ? [livePlan] : plans;
  const marketOverride=config.cachedVariantShippingTexts?.variant_shipping_texts?.[String(variantId)];
  const override=marketOverride || stoqData.overrides?.[String(variantId)];
  return preorderFor(variantId,inventory[String(variantId)],source,{policy:inventory[String(variantId)]?.policy,shippingOverride:override,marketId:config.marketId || config.cachedVariantShippingTexts?.shopify_market_id,selectedPlanId});
}

function renderBox(box,result,cart=false) {
  box.dataset.dispatchType = result.type || 'unknown';
  const known = result.status === 'known';
  const stockLabel = !cart && box.querySelector('[data-dispatch-stock]');
  if(stockLabel){
    stockLabel.hidden = !known;
    stockLabel.textContent = known ? words(result.type==='waller' ? 'stock_local' : result.type==='mixed' ? 'stock_mixed' : 'stock_supplier') : '';
  }
  const titleKey = !known ? 'unknown_title' : result.type==='waller' ? 'local_title' : cart ? 'mixed_title' : 'supplier_title';
  const detailKey = !known ? 'unknown_detail' : result.type==='waller' ? 'local_detail' : result.type==='mixed' ? 'mixed_detail' : cart ? 'supplier_cart' : 'supplier_detail';
  box.querySelector('[data-dispatch-title]').textContent = words(titleKey,result);
  box.querySelector('[data-dispatch-detail]').textContent = words(detailKey,result);
  if(result.items?.some(stockShortage))box.querySelector('[data-dispatch-detail]').textContent=words('preorder_contact');
  const preorder=result.status==='preorder';
  if(preorder){
    const estimates=[...new Set(result.items.filter(item=>item.status==='preorder').map(item=>item.shippingText).filter(Boolean))];
    const expired=result.items.some(item=>item.status==='preorder' && ['expired','unconfirmed'].includes(item.estimateState));
    const missing=result.items.some(item=>item.status==='preorder' && !item.shippingText);
    box.querySelector('[data-dispatch-title]').textContent=words(cart?'preorder_cart_title':'preorder_title');
    box.querySelector('[data-dispatch-detail]').textContent=expired ? [words('preorder_contact'),cart?words('preorder_cart_detail'):''].filter(Boolean).join(' ') : cart ? [words('preorder_cart_detail'),...estimates,missing?words('preorder_cart_fallback'):''].filter(Boolean).join(' ') : estimates[0] || words('preorder_fallback');
  }
  if(result.status==='unknown'){
    box.querySelector('[data-dispatch-title]').textContent='Please check availability';
    box.querySelector('[data-dispatch-detail]').textContent='We cannot confirm availability or a dispatch estimate for this item right now. Please contact Waller BMX before ordering.';
  }
  const note=box.querySelector('[data-dispatch-note]');
  note.hidden = !preorder && (!known || result.type==='waller');
  note.textContent=words(preorder?'preorder_note':'express_note');
  const options = box.querySelector('[data-dispatch-options]');
  if (options) {
    options.replaceChildren();
    for (const [label,delivery] of [['standard_label','standard'],['express_label','express']]) {
      const card=document.createElement('div');card.className='waller-dispatch__option';
      const name=document.createElement('strong');name.textContent=words(label);
      const wait=document.createElement('span');wait.textContent=result.type==='waller'?words('local_title'):words('dispatch_range',result);
      const journey=document.createElement('span');journey.textContent=words(delivery);
      card.append(name);if(known)card.append(wait);card.append(journey);options.append(card);
    }
  }
}

async function updateProduct(box) {
  const area=box.closest('[data-section-type="product"]') || box.closest('.shopify-section') || document;
  // Keep back-in-stock alerts available for sold-out items, but not active preorders.
  const form=area.querySelector('form[action*="/cart/add"]');
  const variantId=form?.querySelector('[name="id"]')?.value || box.dataset.variantId;
  const quantity=Math.max(1,Number(form?.querySelector('[name="quantity"]')?.value || area.querySelector('[name="quantity"]')?.value || 1));
  const shippingVariants=JSON.parse(box.querySelector('[data-dispatch-variants]')?.textContent || '{}');
  const notify=area.querySelector('[data-waller-backinstock="'+box.dataset.productId+'"]');
  if(shippingVariants[String(variantId)]===false){box.hidden=true;if(notify)notify.hidden=true;box.dataset.requestSequence=String(Number(box.dataset.requestSequence || 0)+1);return;}
  box.hidden=false;if(notify)notify.hidden=true;
  const stockLabel=box.querySelector('[data-dispatch-stock]');
  if(stockLabel){stockLabel.hidden=true;stockLabel.textContent='';}
  const sequence=String(Number(box.dataset.requestSequence || 0)+1);box.dataset.requestSequence=sequence;
  try {
    const inventory=await inventoryFor([{variantId,productId:box.dataset.productId,handle:box.dataset.productHandle}]);
    if (box.dataset.requestSequence!==sequence) return;
    const stoqConfig=window._RestockRocketConfig || {};
    const offers=Array.isArray(stoqConfig.sellingPlans) && stoqConfig.sellingPlans.length ? stoqConfig.sellingPlans : stoqData.plans || [];
    const soldOut=soldOutWithoutPreorder(variantId,inventory[String(variantId)],offers,{policy:inventory[String(variantId)]?.policy,marketId:stoqConfig.marketId});
    box.hidden=soldOut;
    if(notify)notify.hidden=!soldOut;
    if(soldOut)return;
    const preorder=preorderEstimate(variantId,inventory);
    const result=withPreorders(estimateDispatch([{variantId,quantity}],inventory,configuration),preorder?{[variantId]:preorder}:{});
    renderBox(box,result);
    if(result.status==='known' || result.status==='preorder') area.querySelectorAll('.iia-container').forEach(widget=>{
      const appBlock=widget.closest('.shopify-app-block');
      if(appBlock)appBlock.hidden=true;else widget.hidden=true;
    });
  } catch {
    if(box.dataset.requestSequence===sequence)renderBox(box,{status:'unknown'});
  }
}

async function updateCart() {
  const boxes=[...document.querySelectorAll('[data-waller-dispatch-cart]')];
  if(!boxes.length)return;
  const current=++revision;
  try {
    const response=await fetch(root+'cart.js',{signal:AbortSignal.timeout(8000),cache:'no-store'});
    if(!response.ok)throw new Error('Cart unavailable.');
    const cart=await response.json();
    if(current!==revision)return;
    boxes.forEach(box=>box.hidden=cart.item_count===0);
    if(!cart.item_count)return;
    const items=cart.items.filter(item=>item.requires_shipping!==false);
    const lines=items.map(item=>({variantId:String(item.variant_id),quantity:item.quantity,productId:item.product_id,handle:item.handle,preorder:false}));
    const inventory=await inventoryFor(lines);
    if(current!==revision)return;
    const overrides={};
    for(const item of items){const estimate=preorderEstimate(item.variant_id,inventory,item.selling_plan_allocation?.selling_plan?.id);if(estimate)overrides[String(item.variant_id)]=estimate;}
    const result=withPreorders(estimateDispatch(lines,inventory,configuration),overrides);
    if(result.status==='no_shipping'){boxes.forEach(box=>box.hidden=true);return;}
    boxes.forEach(box=>renderBox(box,result,true));
    document.querySelectorAll('[data-waller-dispatch-item]').forEach(row=>{
      const item=result.items?.find(item=>item.variantId===row.dataset.variantId);
      if(item?.status==='preorder'){
        row.dataset.dispatchType='preorder';
        if(['expired','unconfirmed'].includes(item.estimateState))row.textContent=words('preorder_contact');
        else if(item.shippingText)row.textContent=words('item_preorder')+item.shippingText;
        else{const link=document.createElement('a');link.textContent=words('item_preorder_fallback');link.href=root+'products/'+encodeURIComponent(row.dataset.productHandle || '')+'?variant='+encodeURIComponent(row.dataset.variantId);row.replaceChildren(link);}
        return;
      }
      if(!item || item.status!=='known'){row.textContent=words(stockShortage(item)?'preorder_contact':'unknown_title');return;}
      const local=item.allocations.every(a=>a.source==='waller');
      row.dataset.dispatchType=local?'waller':'supplier';
      row.textContent=words(local?'item_local':'item_supplier',item);
    });
  }catch{
    if(current===revision)boxes.forEach(box=>renderBox(box,{status:'unknown'},true));
  }
}

function refresh() {
  clearTimeout(timer);
  timer=setTimeout(()=>{
    document.querySelectorAll('[data-waller-dispatch-product]').forEach(updateProduct);
    updateCart();
  },100);
}

document.addEventListener('change',refresh);
document.addEventListener('input',event=>{if(event.target.name==='quantity')refresh();});
for(const event of ['theme:cart:change','theme:cart:added','theme:cart:open','variant:change','product:variant-change','shopify:section:load','stoq:product-changed','restock-rocket:loaded'])document.addEventListener(event,refresh);
const observer=new MutationObserver(records=>{
  if(records.some(record=>[...record.addedNodes].some(node=>node.nodeType===1 && (node.matches('[data-waller-dispatch-item],[data-waller-dispatch-product]')||node.querySelector('[data-waller-dispatch-item],[data-waller-dispatch-product]')))))refresh();
});
observer.observe(document.body,{childList:true,subtree:true});
window.addEventListener('stoq:loaded',refresh);
window.addEventListener('stoq:preorder-payment-option:selected',refresh);
window.addEventListener('pageshow',refresh);
refresh();
